import {ReactElement, RefCallback, useCallback, useRef, useState} from 'react';

import {ReviewVideoEmbedType} from './review-embed-video-helper';
import {ReviewPreviewVideoPlayer} from './review-preview-video-player';
import {ReviewPreviewVideoStreamEmbed} from './review-preview-video-stream-embed';
import {applyPreviewVolume, PreviewVideoHandleType, stopPreviewVideos} from './review-preview-video-types';
import * as styles from './review-preview-video.scss';

/** Ant Design Image has no Video component; video preview uses Image.preview.imageRender. */
export const PREVIEW_IMAGE_PLACEHOLDER_SRC =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function renderEmptyPreviewToolbar(): null {
    return null;
}

export function usePreviewVideoController(): {
    previewClassName: string;
    renderEmptyPreviewToolbar: typeof renderEmptyPreviewToolbar;
    renderVideo: (src: string, embedType?: ReviewVideoEmbedType) => ReactElement | null;
    stop: () => void;
    videoRef: RefCallback<PreviewVideoHandleType | null>;
} {
    const activeVideoRef = useRef<PreviewVideoHandleType | null>(null);
    const [embedGeneration, setEmbedGeneration] = useState(0);

    const stop = useCallback((): void => {
        stopPreviewVideos(activeVideoRef.current);
        activeVideoRef.current = null;
        setEmbedGeneration((generation) => generation + 1);
    }, []);

    const videoRef = useCallback<RefCallback<PreviewVideoHandleType | null>>((node) => {
        activeVideoRef.current = node;

        if (node) {
            applyPreviewVolume(node);
        }
    }, []);

    const renderVideo = useCallback(
        (src: string, embedType: ReviewVideoEmbedType = 'direct') => {
            if (embedType === 'iframe') {
                return <ReviewPreviewVideoStreamEmbed key={`${embedGeneration}-${src}`} src={src} />;
            }

            return <ReviewPreviewVideoPlayer key={`${embedGeneration}-${src}`} src={src} videoRef={videoRef} />;
        },
        [embedGeneration, videoRef]
    );

    return {
        previewClassName: styles.ReviewPreviewVideo_preview,
        renderEmptyPreviewToolbar,
        renderVideo,
        stop,
        videoRef,
    };
}
